













































import { Component, Vue, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import { inject } from 'inversify-props'
import KYCForm from '@company-common/model/kyc-form/KYCForm'
import CustomExistingPortfolioV2 from '@company-common/model/kyc-form/existing-plans/CustomExistingPortfolioV2'
import { FormOptions } from '@master/model/kyc-form/FormOptions'
import { FormStructure } from '@master/model/structure/FormStructure'
import AppRadioSelection from '@core/components/AppRadioSelection.vue'
import TheCurrentPlansGallery from '@/views/kyc-form/component/currentPlans/TheCurrentPlansGallery.vue'
import TheCurrentPlansSummary from '@core/components/currentPlans/TheCurrentPlansSummary.vue'
import TheCurrentPlansDialog from '@/views/kyc-form/component/currentPlans/TheCurrentPlansDialog.vue'
import { InsuranceCompanyList } from '@company-common/config/insurance-company'
import { ProjectInfo } from '@company-common/config/ProjectInfo'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'
import { PolicyTypeOptions } from '@master/model/options/PolicyTypeOptions'

const kycFormStore = namespace('KYCFormStore')

@Component({
  components: {
    AppRadioSelection,
    TheCurrentPlansGallery,
    TheCurrentPlansSummary,
    TheCurrentPlansDialog
  }
})
export default class YourCurrentPlansPage extends Vue {
  @kycFormStore.State
  private kycForm!: KYCForm;

  @kycFormStore.State
  private formOptions!: FormOptions;

  @inject()
  private projectInfo: ProjectInfo

  @inject()
  private sharedVariable: SharedVariable

  uiService: UIService

  private showModal = false
  private mode: 'edit' | 'create' = 'create'
  private isEdit = false
  private selectedPortfolio: CustomExistingPortfolioV2
  private editingIndex = null

  get existingPortfolio () {
    return this.kycForm.existingPortfolio
  }

  get portfolios () {
    return this.existingPortfolio.portfolios
  }

  get isClient2Included () {
    return this.kycForm.client2.personalDetails.included
  }

  get insuranceCompanyList () {
    return InsuranceCompanyList.map(company => {
      return {
        label: company,
        value: company
      }
    })
  }

  get portfolioStructure (): FormStructure<CustomExistingPortfolioV2> {
    return [
      {
        label: 'Policy Name',
        key: 'policyName'
      },
      {
        label: 'Policy Owner (PO)',
        key: 'owner',
        options: this.formOptions?.clientOptions || []
      },
      {
        label: 'Policy Type',
        key: 'policyType',
        options: PolicyTypeOptions
      },
      {
        label: 'Life Assured',
        key: 'policyInsured',
        options: this.formOptions?.assuredOptions || []
      },
      {
        label: 'Insurance Company',
        key: 'companyName',
        options: this.insuranceCompanyList
      },
      {
        label: 'Annual Premium ($)',
        key: 'premium',
        type: 'currency'
      },
      {
        label: 'Policy Term',
        key: 'policyTerms'
      },
      {
        label: 'Death ($)',
        key: 'lifeProtection',
        type: 'currency'
      },
      {
        label: 'Total Permanent Disability ($)',
        key: 'disabilityProtection',
        type: 'currency'
      },
      {
        label: 'Personal Accident ($)',
        key: 'accidentProtection',
        type: 'currency'
      },
      {
        label: 'Critical Illness ($)',
        key: 'criticalIllnessProtection',
        type: 'currency'
      },
      {
        label: 'Maturity Age',
        key: 'maturityAge',
        type: 'number'
      }
    ]
  }

  get summaryColumns () {
    return new CustomExistingPortfolioV2().getSummaryFields().map(field => {
      return {
        attrName: field.attrName,
        abbre: field.abbre + ' ($)'
      }
    })
  }

  get summary () {
    const summary = {
      c1: this.existingPortfolio.summary.existingPortfolioSummary.c1,
      c2: {},
      others: this.existingPortfolio.summary.existingPortfolioSummary.others
    }
    if (this.isClient2Included) {
      summary.c2 = this.existingPortfolio.summary.existingPortfolioSummary.c2
    }
    return summary
  }

  createPortfolio () {
    this.selectedPortfolio = new CustomExistingPortfolioV2(this.formOptions)
    this.mode = 'create'
    this.showModal = true
  }

  editPortfolio (index) {
    this.selectedPortfolio = new CustomExistingPortfolioV2(this.formOptions)
    const portfolio = this.portfolios[index]
    Object.assign(this.selectedPortfolio, portfolio)
    this.editingIndex = index
    this.mode = 'edit'
    this.isEdit = true
    this.showModal = true
  }

  async removePortfolio (index) {
    const isConfirm = await this.uiService.confirm.show({
      type: 'warning',
      message: 'Are yo sure you want to remove this plan?'
    })
    if (isConfirm) {
      const portfolioIndex = index
      this.existingPortfolio.portfolios.splice(portfolioIndex, 1)
      this.calculateSummary()
      this.$emit('save')
    }
  }

  addPortfolio (portfolio: CustomExistingPortfolioV2) {
    const newPortfolio = Object.assign(new CustomExistingPortfolioV2(this.formOptions), portfolio)
    this.portfolios.push(newPortfolio)
    this.showModal = false
    this.calculateSummary()
    this.updateCashFlowValue()
    this.$emit('save')
  }

  savePortfolio (portfolio: CustomExistingPortfolioV2) {
    if (this.isEdit) {
      this.$set(this.portfolios, this.editingIndex, portfolio)
      this.isEdit = false
    } else {
      this.portfolios.push(portfolio)
    }
    this.showModal = false
    this.calculateSummary()
    this.updateCashFlowValue()
    this.$emit('save')
  }

  updateCashFlowValue () {
    const summary = this.existingPortfolio.summary.existingPortfolioSummary as any
    this.kycForm.client1.cashFlow.insurancePremiums = summary.c1.premium
    this.kycForm.client2.cashFlow.insurancePremiums = summary.c2.premium
  }

  calculateSummary () {
    this.existingPortfolio.summary.c1PortfolioSummary()
    this.existingPortfolio.summary.othersPortfolioSummary()
    if (this.isClient2Included) this.existingPortfolio.summary.c2PortfolioSummary()
  }

  closeModal () {
    this.showModal = false
  }

  @Watch('existingPortfolio.declaration.review')
  planningChanged (newValue: string) {
    if (newValue === 'yes') {
      this.existingPortfolio.declaration.reviewReasons = ''
    }
  }

  mounted () {
    this.uiService = this.sharedVariable.uiService

    this.calculateSummary()
  }
}
